/**
 * Phase 3.94 — Section-Sprung-Sidebar für CreateListingPage.
 *
 * Kleines schwebendes Navigations-Panel links auf großen Bildschirmen:
 *  - Listet alle Hauptsektionen mit Fortschritts-Indikator (z. B. 6/8 Felder).
 *  - Klick scrollt smooth zur Sektion und expandiert sie, falls eingeklappt.
 *
 * `form` wird benutzt um zu zählen, wie viele "Pflicht-Indikator"-Felder pro
 * Sektion gefüllt sind — rein heuristisch, kein 1:1-Mapping zur Pflicht-
 * validierung. Soll dem Händler primär die Orientierung geben.
 */
import { useMemo } from "react";
import { CheckCircle2, Circle } from "lucide-react";

function countFilled(form, keys) {
    let total = 0;
    let filled = 0;
    for (const k of keys) {
        total += 1;
        const v = form[k];
        if (Array.isArray(v) ? v.length > 0 : (v !== "" && v !== null && v !== undefined && v !== false)) {
            filled += 1;
        }
    }
    return { filled, total };
}

export default function SectionSidebar({ form, compact = false }) {
    const sections = useMemo(() => {
        const isMoto = form.category === "motorcycle";
        return [
            {
                id: "basics-section",
                label: "Fahrzeug-Stammdaten",
                ...countFilled(form, [
                    "title", "brand", "model",
                    isMoto ? "vehicle_type" : "bike_type",
                    "condition", "price", "first_registration_date",
                    "previous_owners", "mileage", "color_base",
                    ...(isMoto ? ["displacement", "power_kw", "drive_type", "fuel", "transmission"] : []),
                ]),
            },
            { id: "features-section",          label: "Ausstattung & Zubehör", filled: 0, total: 1 },
            { id: "service-section",           label: "Service & Wartung",  ...countFilled(form, ["last_service_date", "service_notes"]) },
            { id: "image-upload-section",      label: "Bilder & Video",     ...countFilled(form, ["images"]) },
            { id: "ad-reach-section-toggle",   label: "Werbung & Reichweite", filled: 0, total: 1 },
            { id: "disposition-section-toggle", label: "Disposition", ...countFilled(form, ["has_coc", "has_zb1", "has_zb2"]) },
            { id: "verkaufsabwicklung-section-toggle", label: "Verkaufsabwicklung", filled: 0, total: 1 },
        ];
    }, [form]);

    const overall = sections.reduce(
        (acc, s) => ({ filled: acc.filled + s.filled, total: acc.total + s.total }),
        { filled: 0, total: 0 },
    );
    const pct = overall.total > 0 ? Math.round((overall.filled / overall.total) * 100) : 0;

    const jumpTo = (id) => {
        const el = document.querySelector(`[data-testid="${id}"]`);
        if (!el) return;
        // Expand collapsed parent if needed
        const collapsedBtn = el.querySelector('[aria-expanded="false"]');
        if (collapsedBtn) collapsedBtn.click();
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 150);
    };

    // Feb-2026 — Compact-Modus: horizontale Mini-Variante, passt unter
    // „Noch fehlend für die Such-Card" in LiveListingPreview.
    if (compact) {
        return (
            <div className="bg-white border border-gray-200 rounded-sm p-2.5 shadow-sm" data-testid="section-sidebar-compact">
                <div className="flex items-baseline justify-between mb-1">
                    <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500">Fortschritt</div>
                    <div className="flex items-baseline gap-1">
                        <span className="font-display font-black text-base font-mono-num leading-none">{pct}%</span>
                        <span className="text-[9px] text-gray-500 font-mono-num">{overall.filled}/{overall.total}</span>
                    </div>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-sm overflow-hidden mb-2">
                    <div className="h-full bg-[#FFCC00] transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <div className="flex flex-wrap gap-1">
                    {sections.map((s) => {
                        const done = s.total > 0 && s.filled >= s.total;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                onClick={() => jumpTo(s.id)}
                                className={`text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border transition-colors ${done ? "bg-emerald-50 text-emerald-800 border-emerald-300" : "bg-gray-50 text-gray-600 border-gray-200 hover:border-[#111827] hover:text-[#111827]"}`}
                                data-testid={`sidebar-jump-${s.id}`}
                                title={`${s.label} ${s.total > 1 ? `(${s.filled}/${s.total})` : ""}`}
                            >
                                {done ? <CheckCircle2 className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" /> : <Circle className="w-2.5 h-2.5 inline -mt-0.5 mr-0.5" />}
                                {s.label.split(" ")[0]}
                            </button>
                        );
                    })}
                </div>
            </div>
        );
    }

    return (
        <aside
            className="hidden xl:block fixed left-4 top-32 w-44 z-20"
            data-testid="section-sidebar"
            aria-label="Sektions-Navigation"
        >
            <div className="bg-white border border-gray-200 rounded-sm p-3 shadow-sm">
                <div className="text-[10px] font-bold uppercase tracking-widest text-gray-500 mb-1">Fortschritt</div>
                <div className="flex items-baseline justify-between mb-2">
                    <span className="font-display font-black text-2xl font-mono-num">{pct}%</span>
                    <span className="text-[10px] text-gray-500 font-mono-num">{overall.filled}/{overall.total}</span>
                </div>
                <div className="h-1.5 bg-gray-100 rounded-sm overflow-hidden mb-3">
                    <div className="h-full bg-[#FFCC00] transition-all duration-300" style={{ width: `${pct}%` }} />
                </div>
                <ul className="space-y-1">
                    {sections.map((s) => {
                        const done = s.total > 0 && s.filled >= s.total;
                        const Icon = done ? CheckCircle2 : Circle;
                        return (
                            <li key={s.id}>
                                <button
                                    type="button"
                                    onClick={() => jumpTo(s.id)}
                                    className="w-full flex items-start gap-1.5 text-left text-[11px] hover:bg-gray-50 rounded-sm px-1.5 py-1 transition-colors"
                                    data-testid={`sidebar-jump-${s.id}`}
                                >
                                    <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${done ? "text-emerald-600" : "text-gray-300"}`} strokeWidth={2.4} />
                                    <div className="flex-1 min-w-0">
                                        <div className="font-medium text-gray-800 leading-tight truncate">{s.label}</div>
                                        {s.total > 1 && (
                                            <div className="text-[9px] text-gray-400 font-mono-num">{s.filled}/{s.total}</div>
                                        )}
                                    </div>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </aside>
    );
}
